import Axios, { AxiosError, AxiosInstance } from 'axios';
import Storage, { STORAGE_KEYS } from '@/utils/helpers/Storage';
import RequestQueue from '@/lib/auth/requestQueue';
import { API_ERROR_CODES, COMMON_ERRORS, ENV_VARIABLES } from '@/consts/common';
import { refreshAccessToken } from '@/lib/auth/authInterceptor';
import { getApiErrorCode } from '@/lib/auth/apiErrors';
import { notifyUsageTimeLimitLockout } from '@/lib/auth/usageTimeLimitLockout';

const {
  VITE_AWS_HOLOXR_BACKEND_API_BASEURL,
  VITE_AWS_USERS_BACKEND_API_BASEURL,
} = ENV_VARIABLES;

if (!VITE_AWS_HOLOXR_BACKEND_API_BASEURL) {
  throw new Error('Environment variable not set: VITE_AWS_HOLOXR_BACKEND_API_BASEURL');
}
if (!VITE_AWS_USERS_BACKEND_API_BASEURL) {
  throw new Error('Environment variable not set: VITE_AWS_USERS_BACKEND_API_BASEURL');
}

const sharedRequestQueue = new RequestQueue();

/** Shared Axios response-error path used by both API clients. Exported for unit tests. */
export function handleApiResponseError(
  err: AxiosError,
  client: AxiosInstance,
): Promise<unknown> {
  const status = err?.response?.status;
  if (status === 401) {
    return refreshAccessToken(sharedRequestQueue, client, err);
  }

  const errorCode = getApiErrorCode(err?.response?.data);
  if (errorCode === API_ERROR_CODES.USAGE_TIME_LIMIT) {
    notifyUsageTimeLimitLockout();
    return Promise.reject(new Error(API_ERROR_CODES.USAGE_TIME_LIMIT));
  }

  return Promise.reject(new Error(errorCode ?? COMMON_ERRORS.UNEXPECTED_ERROR));
}

function createApiClient(baseURL: string): AxiosInstance {
  const client = Axios.create({ baseURL });

  client.interceptors.request.use(
    (config) => {
      const accessToken = Storage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      if (accessToken) {
        // eslint-disable-next-line no-param-reassign
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      return config;
    },
  );

  client.interceptors.response.use(
    (fulfilled) => fulfilled,
    (err: AxiosError) => handleApiResponseError(err, client),
  );

  return client;
}

const usersApiClient = createApiClient(VITE_AWS_USERS_BACKEND_API_BASEURL);
const holoxrApiClient = createApiClient(VITE_AWS_HOLOXR_BACKEND_API_BASEURL);

export { holoxrApiClient, usersApiClient };
